/**
 * Escapes HTML special characters in a string to prevent XSS attacks.
 *
 * @param {string} text - The text to escape.
 * @returns {string} - The escaped text.
 */
export function htmlEscape(text) {
	const div = document.createElement('div');
	div.textContent = text;
	return div.innerHTML;
}

/**
 * Adds a loading overlay to the specified element.
 *
 * @param {HTMLElement} es6Box - The element to which the overlay will be added.
 */
export function addOverlay(es6Box) {
	const spinner = document.createElement('div');
	spinner.className = 'site-inner--loading__overlay__spinner';
	const overlay = document.createElement('div');
	overlay.className = 'site-inner--loading__overlay';
	overlay.appendChild(spinner);
	es6Box.appendChild(overlay);
	es6Box.classList.add('site-inner--loading');
}

/**
 * Removes the loading overlay from the specified element.
 *
 * @param {HTMLElement} es6Box - The element from which the overlay will be removed.
 */
export function removeOverlay(es6Box) {
	const overlay = es6Box.querySelector('.site-inner--loading__overlay');
	if (overlay) {
		es6Box.removeChild(overlay);
		es6Box.classList.remove('site-inner--loading');
	}
}

export async function queryApi(endpoint, data) {
	if (!endpoint || !data) {
		throw new Error('Missing required parameters');
	}
	
	const url = `${hmk_rest_var.rest_url}${endpoint}`;
	const body = JSON.stringify({
		...data,
		action_nonce: hmk_rest_var.action_nonce
	});
	
	const response = await fetch(url, {
		headers: {
			"X-WP-Nonce": hmk_rest_var.nonce,
			"Content-Type": "application/json",
		},
		method: "POST",
		credentials: "same-origin",
		body,
	});
	
	const responseData = await response.json();
	
	if (!response.ok) {
		throw new Error(responseData.message || 'Unknown error');
	}
	
	return responseData;
}

export async function queryAdminApi(param) {
	// console.log({ param });
	
	// Validate that param is an object
	if (typeof param !== 'object') {
		return Promise.reject(new Error('param not object'));
	}
	
	// Validate that 'data' key exists in param
	if (!('data' in param)) {
		return Promise.reject(new Error('param missing data'));
	}
	
	// Initialize API path
	let api = '';
	if ('api' in param) {
		api = param['api'] + '/';
	}
	// console.log({ api });
	
	// Determine HTTP method based on 'endpoint'
	let method = 'POST';
	if (!('endpoint' in param)) {
		return Promise.reject(new Error('param missing endpoint'));
	} else if (param['endpoint'] === 'read') {
		method = 'GET';
	}
	// console.log({ method });
	
	// Construct base URL
	const baseEndpoint = `${hmk_api_var.rest_endpoint}${api}${param['endpoint']}/a/${encodeURIComponent(param['data']['hmka'])}`;
	// Remove 'hmka' from data as it's now part of the URL
	const { hmka, ...dataWithoutHmka } = param['data'];
	
	// If method is POST, prepare the body
	let url = baseEndpoint;
	let fetchOptions = {
		headers: new Headers({
			"X-WP-Nonce": hmk_api_var.rest_nonce,
			// 'Content-Type' is typically not needed for GET requests
		}),
		method: method,
		credentials: "same-origin",
	};
	
	dataWithoutHmka['action_nonce'] = hmk_api_var.action_nonce;
	if (method === 'POST') {
		// Add 'action_nonce' only for POST requests if needed
		
		fetchOptions.headers.append("Content-Type", "application/json");
		fetchOptions.body = JSON.stringify(dataWithoutHmka);
	} else if (method === 'GET') {
		// For GET requests, append data as query parameters
		const queryParams = new URLSearchParams(dataWithoutHmka).toString();
		url += `?${queryParams}`;
	}
	
	// console.log({ url });
	// console.log({ fetchOptions });
	
	try {
		const response = await fetch(url, fetchOptions);
		
		// Check if the response status is within the success range
		if (!response.ok) {
			const errorBody = await response.text();
			let errorMessage = `HTTP error! status: ${response.status}`;
			try {
				const errorJson = JSON.parse(errorBody);
				if (errorJson.message) {
					errorMessage = errorJson.message;
				}
			} catch (e) {
				// If response is not JSON, keep the default error message
			}
			return Promise.reject(new Error(errorMessage));
		}
		
		const responseJson = await response.json();
		
		// Assuming your API returns a 'success' flag
		if (responseJson.success) {
			return responseJson.data;
		} else {
			// If 'success' flag is not present, adjust based on your API's response structure
			return Promise.reject(new Error(responseJson.data || 'Unknown error'));
		}
		
	} catch (error) {
		// Handle network or parsing errors
		return Promise.reject(new Error(error.message || 'Unknown error'));
	}
}

export function numberFormat(number, decimals, dec_point, thousands_sep) {
	// http://kevin.vanzonneveld.net
	// +   original by: Jonas Raoni Soares Silva (http://www.jsfromhell.com)
	// +   improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
	// +     bugfix by: Michael White (http://getsprink.com)
	// +     bugfix by: Benjamin Lupton
	// +     bugfix by: Allan Jensen (http://www.winternet.no)
	// +    revised by: Jonas Raoni Soares Silva (http://www.jsfromhell.com)
	// +     bugfix by: Howard Yeend
	// +    revised by: Luke Smith (http://lucassmith.name)
	// +     bugfix by: Diogo Resende
	// +     bugfix by: Rival
	// +      input by: Kheang Hok Chin (http://www.distantia.ca/)
	// +   improved by: davook
	// +   improved by: Brett Zamir (http://brett-zamir.me)
	// +      input by: Jay Klehr
	// +   improved by: Brett Zamir (http://brett-zamir.me)
	// +      input by: Amir Habibi (http://www.residence-mixte.com/)
	// +     bugfix by: Brett Zamir (http://brett-zamir.me)
	// +   improved by: Theriault
	// +      input by: Amirouche
	// +   improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
	// *     example 1: number_format(1234.56);
	// *     returns 1: '1,235'
	// *     example 2: number_format(1234.56, 2, ',', ' ');
	// *     returns 2: '1 234,56'
	// *     example 3: number_format(1234.5678, 2, '.', '');
	// *     returns 3: '1234.57'
	// *     example 4: number_format(67, 2, ',', '.');
	// *     returns 4: '67,00'
	// *     example 5: number_format(1000);
	// *     returns 5: '1,000'
	// *     example 6: number_format(67.311, 2);
	// *     returns 6: '67.31'
	// *     example 7: number_format(1000.55, 1);
	// *     returns 7: '1,000.6'
	// *     example 8: number_format(67000, 5, ',', '.');
	// *     returns 8: '67.000,00000'
	// *     example 9: number_format(0.9, 0);
	// *     returns 9: '1'
	// *    example 10: number_format('1.20', 2);
	// *    returns 10: '1.20'
	// *    example 11: number_format('1.20', 4);
	// *    returns 11: '1.2000'
	// *    example 12: number_format('1.2000', 3);
	// *    returns 12: '1.200'
	// *    example 13: number_format('1 000,50', 2, '.', ' ');
	// *    returns 13: '100 050.00'
	// Strip all characters but numerical ones.
	number = (number + '').replace(/[^0-9+\-Ee.]/g, '');
	var n = !isFinite(+number) ? 0 : +number,
		prec = !isFinite(+decimals) ? 0 : Math.abs(decimals),
		sep = (typeof thousands_sep === 'undefined') ? ',' : thousands_sep,
		dec = (typeof dec_point === 'undefined') ? '.' : dec_point,
		s = '',
		toFixedFix = function (n, prec) {
			var k = Math.pow(10, prec);
			return '' + Math.round(n * k) / k;
		};
	// Fix for IE parseFloat(0.55).toFixed(0) = 0;
	s = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.');
	if (s[0].length > 3) {
		s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
	}
	if ((s[1] || '').length < prec) {
		s[1] = s[1] || '';
		s[1] += new Array(prec - s[1].length + 1).join('0');
	}
	return s.join(dec);
}

export function formatPrice(price) {
	
	const formatted = hmk_rest_var.currency_symbol + numberFormat(price, hmk_rest_var.decimals, hmk_rest_var.decimal_separator, hmk_rest_var.thousand_separator);
	
	return formatted;
	
}